'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StudioVideoStoreProvider } from '@/features/ai-video-creator/stores/studioVideoStore';
import { Layout, Form, FormLayout, InlineStack, Button, BlockStack } from '@shopify/polaris';
import { ImageBackgroundGeneration } from '@/components/images/ImageBackgroundGeneration';
import ScrollableThumbnailSelect from '@/components/images/ScrollableThumbnailSelect';
import { MediaModalSectionName, SECTIONS_WITHOUT_TOOLS } from '@/features/media-modal/components/common/commonTypes';
import AddMediaModal from '@/features/media-modal/components/AddMediaModal';
import {
  AssetCreationToolName,
  GenerationInstrument,
  MediaAsset,
  MediaAssetType,
  ICardMediaAsset,
  PlacementType,
} from '@/types/media';
import { ImageWithProductId as Image } from '@/types/media';

import { useToast } from '@/hooks/useToast';
import { useImageBackgroundGeneration } from '@/hooks/image';
import { PricesService } from '@/features/billing';
import { useInsufficientBalance } from '@/features/billing/components/InsufficientBalanceContext';
import useSaveBar from '@/hooks/useSaveBar';
import useFormDataState, { FormName } from '@/hooks/useFormDataState';
import usePageNavigation from '@/hooks/usePageNavigation';
import GeneratedMediaCard from '@/components/media-asset/ai-tools/GeneratedMediaCard';
import { Page } from '@/components/Page';
import { MediaAssetPollingProvider } from '@/features/ai-video-creator/components/MediaAssetPollingContext';
import { BannerState, SessionBanner } from '@/types/banners';
import { useQueryClient } from '@tanstack/react-query';
import BasicBanner from '@/components/BasicBanner';
import DismissibleBanner from '@/components/DismissibleBanner';

const CREDITS = PricesService.getPriceText(AssetCreationToolName.replaceImageBackground, [
  GenerationInstrument.imageToImage,
]);
const CREDITS_TEXT = CREDITS ? ` (${CREDITS})` : '';

const ALLOWED_SECTIONS = [
  ...SECTIONS_WITHOUT_TOOLS.filter(
    (section) => ![MediaModalSectionName.bunuVideos, MediaModalSectionName.playApp].includes(section)
  ),
];

export const TAB_IDS = {
  USE_IMAGE: 'use-image-background',
  GENERATE_AI: 'generate-ai-background',
};

export const AIBackgroundAssetCreatorComponent = memo(() => {
  const { notify } = useToast();
  const { checkBalance } = useInsufficientBalance();

  const { formData, handleSetFormData, handleClearFormData } = useFormDataState(FormName.CreateBackground);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<Image | null>(formData.selectedImage || null);
  const [prompt, setPrompt] = useState<string>(formData.prompt || '');
  const [aspectRatio, setAspectRatio] = useState<'9:16' | '16:9' | '1:1'>(formData.aspectRatio || '9:16');
  const [backgroundImage, setBackgroundImage] = useState<Image | null>(formData.backgroundImage || null);
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [selectedTabId, setSelectedTabId] = useState(TAB_IDS.GENERATE_AI);
  const [placementType, setPlacementType] = useState<PlacementType>(
    formData.placementType || PlacementType.ManualPlacement
  );

  const { isGenerating, isError, generatedMediaAsset: generatedMedia, handleGenerate } = useImageBackgroundGeneration();
  const generatedMediaAsset = useMemo(() => generatedMedia?.[0], [generatedMedia]);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedImage(formData.selectedImage || null);
    setPrompt(formData.prompt || '');
    setAspectRatio(formData.aspectRatio || '9:16');
    setBackgroundImage(formData.backgroundImage || null);
    setPlacementType(formData.placementType || PlacementType.ManualPlacement);
  }, [formData]);

  const handleAddMedia = useCallback(
    (media: MediaAsset[]) => {
      const hasVideo = media.some((item) => item.type === MediaAssetType.video);
      if (hasVideo) {
        setError('Videos are not supported for background image');
        return;
      }

      const newImages = media.map((item) => ({
        id: item.id,
        src: item.src,
        alt: item.alt,
        productId: item.provider === 'product' ? item.id : undefined,
      }));
      setSelectedImage(newImages[0]);
    },
    [setSelectedImage]
  );

  const handleSaveDraft = useCallback(() => {
    handleSetFormData({
      selectedImage,
      prompt,
      aspectRatio,
      backgroundImage,
      placementType,
    });
    notify('Draft saved');
  }, [selectedImage, prompt, aspectRatio, backgroundImage, placementType, handleSetFormData, notify]);

  const handleDiscard = useCallback(() => {
    setSelectedImage(formData.selectedImage || null);
    setPrompt(formData.prompt || '');
    setAspectRatio(formData.aspectRatio || '9:16');
    setBackgroundImage(formData.backgroundImage || null);
    setPlacementType(formData.placementType || PlacementType.ManualPlacement);
  }, [formData]);

  const hasFormChanged = useMemo(() => {
    return (
      selectedImage !== formData.selectedImage ||
      prompt !== formData.prompt ||
      aspectRatio !== formData.aspectRatio ||
      backgroundImage !== formData.backgroundImage ||
      placementType !== formData.placementType
    );
  }, [formData, selectedImage, prompt, aspectRatio, backgroundImage, placementType]);

  const isDirty = hasFormChanged && (!!selectedImage || !!prompt || !!backgroundImage);

  const { forceHideSaveBar } = useSaveBar({
    isDirty,
    onSave: handleSaveDraft as () => void,
    onDiscard: handleDiscard,
  });

  const handleGenerateClick = useCallback(async () => {
    pageRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

    if (!checkBalance(AssetCreationToolName.replaceImageBackground, [GenerationInstrument.imageToImage])) {
      return;
    }
    forceHideSaveBar();
    await handleGenerate({
      imageSrc: selectedImage?.src,
      aspectRatio,
      ...(selectedTabId === TAB_IDS.GENERATE_AI ? { prompt } : {}),
      placementType,
      ...(selectedTabId === TAB_IDS.USE_IMAGE ? { backgroundImageSrc: backgroundImage?.src } : {}),
    });
    handleClearFormData(true);
  }, [
    handleGenerate,
    checkBalance,
    selectedImage,
    prompt,
    aspectRatio,
    backgroundImage,
    placementType,
    selectedTabId,
    handleClearFormData,
    forceHideSaveBar,
  ]);

  const handleImageClickMore = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleAddBackgroundImage = useCallback(() => {
    setIsBackgroundModalOpen(true);
  }, []);

  const handleAddBackgroundMedia = useCallback((media: MediaAsset[]) => {
    if (media.length === 0) return;

    const mediaAsset = media[0];
    if (mediaAsset.type === MediaAssetType.video) {
      setError('Videos are not supported for background image');
      return;
    }

    setBackgroundImage(mediaAsset as any);
  }, []);

  const { navigateBack, previousPageTitle } = usePageNavigation();

  const isGenerateDisabled = useMemo(() => {
    if (selectedTabId === TAB_IDS.GENERATE_AI) {
      return !prompt.trim() || !selectedImage || isGenerating;
    } else if (selectedTabId === TAB_IDS.USE_IMAGE) {
      return !backgroundImage || !selectedImage || isGenerating;
    }
    return true;
  }, [selectedTabId, prompt, backgroundImage, selectedImage, isGenerating]);

  return (
    <>
      <Page
        backAction={{ content: previousPageTitle, onAction: navigateBack }}
        title="Replace background"
        ref={pageRef}
      >
        <BlockStack gap="400">
          <BasicBanner tone="critical" title="Invalid media" hidden={!error} onDismiss={() => setError('')}>
            {error}
          </BasicBanner>
          <DismissibleBanner
            tone="critical"
            title="Generation failed"
            sessionBanner={SessionBanner.BackGroundGenerationError}
            showBanner={isError}
          >
            Error generating background. Please try again or contact support.
          </DismissibleBanner>

          <Layout>
            <Layout.Section>
              <Form noValidate onSubmit={() => {}}>
                <FormLayout>
                  <ScrollableThumbnailSelect
                    title="Select an image"
                    description="Choose or create an image to work with"
                    items={selectedImage ? [selectedImage] : []}
                    selected={selectedImage}
                    onSelect={setSelectedImage}
                    loading={isGenerating}
                    onMoreClick={handleImageClickMore}
                  />

                  <ImageBackgroundGeneration
                    prompt={prompt}
                    isPromptLoading={false}
                    isGenerating={isGenerating}
                    isError={isError}
                    setPrompt={setPrompt}
                    aspectRatio={aspectRatio}
                    setAspectRatio={setAspectRatio}
                    backgroundImage={backgroundImage}
                    setBackgroundImage={setBackgroundImage}
                    onAddBackgroundImage={handleAddBackgroundImage}
                    selectedTabId={selectedTabId}
                    setSelectedTabId={setSelectedTabId}
                    placementType={placementType}
                    setPlacementType={setPlacementType}
                  />

                  <AddMediaModal
                    allowedSections={ALLOWED_SECTIONS}
                    mediaAssetType={MediaAssetType.image}
                    addMedia={handleAddMedia}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    initialSection={MediaModalSectionName.product}
                    isOnlyImages
                    multiSelection={false}
                  />

                  <AddMediaModal
                    allowedSections={ALLOWED_SECTIONS}
                    mediaAssetType={MediaAssetType.image}
                    addMedia={handleAddBackgroundMedia}
                    isOpen={isBackgroundModalOpen}
                    setIsOpen={setIsBackgroundModalOpen}
                    initialSection={MediaModalSectionName.upload}
                    isOnlyImages
                  />
                </FormLayout>
              </Form>
            </Layout.Section>
            <Layout.Section variant="oneThird">
              <GeneratedMediaCard
                title="Generated"
                subtitle="Select an image and enter a prompt to generate a new background"
                media={generatedMediaAsset as ICardMediaAsset}
                mediaType="Image"
              />
            </Layout.Section>
            <Layout.Section>
              <InlineStack align="end">
                <Button onClick={handleGenerateClick} variant="primary" disabled={isGenerateDisabled}>
                  {`Generate${CREDITS_TEXT}`}
                </Button>
              </InlineStack>
            </Layout.Section>
          </Layout>
        </BlockStack>
      </Page>
    </>
  );
});

AIBackgroundAssetCreatorComponent.displayName = 'AIBackgroundAssetCreatorComponent';

export const AIBackgroundAssetCreator = (props) => {
  const queryClient = useQueryClient();
  const initStateFromServerPage = useRef(false);

  if (!initStateFromServerPage.current) {
    initStateFromServerPage.current = true;
    if (props.isBackgroundGenerationErrorBannerHidden) {
      queryClient.setQueryData(['bannerState', SessionBanner.BackGroundGenerationError], BannerState.CLOSED);
    }
  }

  return (
    <MediaAssetPollingProvider>
      <StudioVideoStoreProvider>
        <AIBackgroundAssetCreatorComponent {...props} />
      </StudioVideoStoreProvider>
    </MediaAssetPollingProvider>
  );
};
